import { useQuery } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TriangleAlert } from "lucide-react";

import { StandardPageLayout } from "@/components/layouts/standard-page-layout";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchCredits,
  fetchPaymentMethod,
  fetchSubscription,
} from "data/queries-common";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";

import CreditUseModal from "./modals/credit-use-modal";
import FeatureComparisonModal from "./modals/feature-comparison-modal";

import { CancelSubscriptionCard } from "./cancel-subscription-card";
import { CurrentPlanCard } from "./current-plan-card";
import { OverageCard } from "./overage-card";
import { ScheduledCancellationAlert } from "./scheduled-cancellation-alert";
import { ScheduledPlanChangeAlert } from "./scheduled-plan-change-alert";

export const SubscriptionPage = () => {
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const location = useLocation();

  const [featureComparisonOpen, setFeatureComparisonOpen] = useState(false);
  const [creditUseOpen, setCreditUseOpen] = useState(false);

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });

  const creditsQuery = useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
    enabled: !!subscriptionQuery.data?.activeSubscription,
  });

  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });

  useEffect(() => {
    const routeActions: Record<string, () => void> = {
      [Routes.orgFeatureComparisonTable]: () => setFeatureComparisonOpen(true),
      [Routes.orgFeatureCostTable]: () => setCreditUseOpen(true),
    };
    const action = routeActions[location.pathname];
    if (action) action();
  }, [location.pathname]);

  const active = subscriptionQuery.data?.activeSubscription;
  const hasScheduledUpgradeOrDowngrade = active?.nextSub != null;
  const hasScheduledCancellation =
    active?.cancelAtPeriodEnd === true && active?.trialType !== "admin";

  const isLoading =
    subscriptionQuery.isPending ||
    (creditsQuery.isEnabled && creditsQuery.isPending);
  const isError = subscriptionQuery.isError || creditsQuery.isError;

  return (
    <StandardPageLayout
      pageTitle={{
        icon: CreditCard,
        title: "Subscription",
        description:
          "View your current plan, manage overage, and control your subscription.",
      }}
    >
      <div className="mx-auto max-w-5xl space-y-4">
        {isError && (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>
              An error occurred while fetching the subscription information.
            </AlertDescription>
          </Alert>
        )}

        {subscriptionQuery.isSuccess && active && hasScheduledCancellation && (
          <ScheduledCancellationAlert subscription={subscriptionQuery.data} />
        )}

        {subscriptionQuery.isSuccess &&
          active &&
          hasScheduledUpgradeOrDowngrade && (
            <ScheduledPlanChangeAlert subscription={subscriptionQuery.data} />
          )}

        {isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Current Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <Skeleton className="h-48 w-full" />
            </CardContent>
          </Card>
        )}

        {subscriptionQuery.isSuccess && (
          <CurrentPlanCard
            subscription={subscriptionQuery.data}
            credits={creditsQuery.data ?? undefined}
            hasScheduledUpgradeOrDowngrade={hasScheduledUpgradeOrDowngrade}
            hasScheduledCancellation={hasScheduledCancellation}
          />
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setFeatureComparisonOpen(true)}
          >
            Feature Comparison
          </Button>
          <Button variant="outline" onClick={() => setCreditUseOpen(true)}>
            Feature Costs
          </Button>
        </div>

        {subscriptionQuery.isSuccess &&
          creditsQuery.isSuccess &&
          creditsQuery.data &&
          paymentMethodQuery.isSuccess && (
            <OverageCard
              subscription={subscriptionQuery.data}
              credits={creditsQuery.data}
              paymentMethod={paymentMethodQuery.data}
            />
          )}

        {subscriptionQuery.isSuccess && active && (
          <CancelSubscriptionCard
            subscription={subscriptionQuery.data}
            hasScheduledUpgradeOrDowngrade={hasScheduledUpgradeOrDowngrade}
            hasScheduledCancellation={hasScheduledCancellation}
          />
        )}

        <FeatureComparisonModal
          open={featureComparisonOpen}
          onClose={() => setFeatureComparisonOpen(false)}
        />
        <CreditUseModal
          open={creditUseOpen}
          onClose={() => setCreditUseOpen(false)}
        />
      </div>
    </StandardPageLayout>
  );
};
